/**
 * GST OIO Document Formatter - v2.0
 * ====================================================
 * Generates authentic CBIC-style Order-in-Original documents
 *
 * Official formatting rules:
 * - Typography: Times New Roman (12pt body, 14pt headings, 16pt title)
 * - Line Spacing: 1.5 lines
 * - Paragraph Spacing: 6pt before/after
 * - Alignment: Title centered, body justified
 * - Numbering: Legal hierarchy (1., 1.1., 1.1.1., etc.) via DOCX numbering
 * - Tables: Government style thin borders, header shading #D9D9D9
 * - Numbers: Indian grouping (lakhs/crores) with Rs. prefix
 * - Bold/italic: Preserved from markdown and rendered as proper runs
 */

import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  AlignmentType,
  BorderStyle,
  convertInchesToTwip,
  Header,
  Footer,
  PageNumber,
  LevelFormat,
  Table,
  TableRow,
  TableCell,
  WidthType,
  ShadingType,
  VerticalAlign,
} from "docx"
import type { IRunPropertiesOptions } from "docx"

type DocumentOptions = NonNullable<ConstructorParameters<typeof Document>[0]>

/* ============================================================================
   CONSTANTS - CBIC STANDARD FORMATTING
   ============================================================================ */

export const FONT = "Times New Roman"
export const SIZES = {
  TITLE: 32,        // 16pt
  HEADING: 28,      // 14pt
  BODY: 24,         // 12pt
  TABLE: 22,        // 11pt
  TABLE_HEADER: 22, // 11pt bold
  FOOTER: 20,       // 10pt
  SIGNATURE: 22,    // 11pt
  SMALL: 20,        // 10pt
}

export const SPACING = {
  LINE: 360,              // 1.5 line spacing
  PARA_BEFORE: 60,        // 6pt before
  PARA_AFTER: 60,         // 6pt after
  HEADING_BEFORE: 240,    // 12pt before heading
  HEADING_AFTER: 120,     // 6pt after heading
  SECTION_GAP: 160,       // 8pt gap between major sections
  SIGNATURE_BEFORE: 360,  // 18pt before signature
}

const BORDERS = {
  TABLE: { style: BorderStyle.SINGLE, size: 6, color: "000000" },
}

/* ============================================================================
   TEXT UTILITIES
   ============================================================================ */

/** Indian digit grouping for rupee amounts only: other long digit runs (GSTIN, HSN, pincode, e-way bill) are left alone. */
export function formatIndianNumber(text: string): string {
  return text.replace(/(?<=Rs\.?\s*)(\d{4,})(?![\d,])/g, (num) => {
    const last3 = num.slice(-3)
    const rest = num.slice(0, -3)
    return rest.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + last3
  })
}

function lightClean(text: string): string {
  return text
    .replace(/\t/g, " ")
    .replace(/  +/g, " ")
    .replace(/([.,:;])([A-Za-zऀ-ॿ])/g, "$1 $2")
    .replace(/₹\s*/g, "Rs. ")
    .replace(/Rs\.\s+/g, "Rs. ")
    .replace(/DIN_PENDING/gi, "______________")
    .trim()
}

/* ============================================================================
   INLINE FORMATTING PARSER
   ============================================================================ */

interface TextSegment {
  text: string
  bold: boolean
  italic: boolean
  underline: boolean
}

function parseInlineFormatting(text: string): TextSegment[] {
  const segments: TextSegment[] = []
  const regex = /(\*{1,3}|<u>)((?:(?!\1|<\/u>).)+?)(\1|<\/u>)/gi
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      const before = text.slice(lastIndex, match.index)
      if (before) segments.push({ text: before, bold: false, italic: false, underline: false })
    }

    const markerStart = match[1].toLowerCase()
    const content = match[2]

    if (markerStart === "***") {
      segments.push({ text: content, bold: true, italic: true, underline: false })
    } else if (markerStart === "**") {
      segments.push({ text: content, bold: true, italic: false, underline: false })
    } else if (markerStart === "<u>") {
      segments.push({ text: content, bold: false, italic: false, underline: true })
    } else {
      segments.push({ text: content, bold: false, italic: true, underline: false })
    }

    lastIndex = match.index + match[0].length
  }

  if (lastIndex < text.length) {
    segments.push({ text: text.slice(lastIndex), bold: false, italic: false, underline: false })
  }

  if (segments.length === 0) {
    segments.push({ text, bold: false, italic: false, underline: false })
  }

  return segments
}

export function createTextRun(text: string, options: Partial<IRunPropertiesOptions> = {}): TextRun {
  return new TextRun({
    text: formatIndianNumber(text),
    font: FONT,
    size: SIZES.BODY,
    ...options,
  })
}

export function createFormattedRuns(text: string, baseOptions: Partial<IRunPropertiesOptions> = {}): TextRun[] {
  const segments = parseInlineFormatting(text)
  return segments.map((seg) =>
    createTextRun(seg.text, {
      ...baseOptions,
      bold: seg.bold || baseOptions.bold || false,
      italics: seg.italic || baseOptions.italics || false,
      underline: seg.underline ? { type: "single" } : undefined,
    }),
  )
}

/* ============================================================================
   PARSED ELEMENT TYPES
   ============================================================================ */

export interface ParsedElement {
  type: "title" | "heading" | "subheading" | "numbered" | "bullet" | "paragraph" | "table" | "signature" | "spacer"
  content: string
  level?: number
  number?: string
  bold?: boolean
  rows?: { cells: string[]; isHeader: boolean }[]
}

/* ============================================================================
   MARKDOWN → STRUCTURED ELEMENTS PARSER
   ============================================================================ */

function parseMajorHeading(text: string): { number: string; content: string } | null {
  const plain = text.replace(/^\*+|\*+$/g, "").trim()
  const match = plain.match(/^(\d+)\.\s+([A-Z][A-Z\s\-&,]+)$/)
  if (match) return { number: match[1], content: match[2].trim() }
  const mixedMatch = plain.match(/^(\d+)\.\s+(.+)$/)
  if (mixedMatch && plain.length < 80) {
    const content = mixedMatch[2].trim()
    if (content === content.toUpperCase() || /^[A-Z]/.test(content)) {
      return { number: mixedMatch[1], content }
    }
  }
  return null
}

function parseNumberedParagraph(text: string): { number: string; content: string; level: number } | null {
  const plain = text.replace(/^\*+|\*+$/g, "").trim()
  const match = plain.match(/^(\d+(?:\.\d+)+)\.?\s+([\s\S]+)$/)
  if (match) {
    const number = match[1]
    const content = match[2]
    const level = (number.match(/\./g) || []).length
    return { number, content, level }
  }
  return null
}

function parseSingleNumberParagraph(
  text: string,
  minLength = 80,
): { number: string; content: string } | null {
  const plain = text.replace(/^\*+|\*+$/g, "").trim()
  const match = plain.match(/^(\d+)\.\s+([\s\S]+)$/)
  if (match && match[2].length > minLength) {
    return { number: match[1], content: match[2] }
  }
  return null
}

function isSignatureBlock(text: string): boolean {
  const t = text.trim()
  const patterns = [
    /^\[.*\]$/,
    /^\(.*\)$/,
    /^(Commissioner|Asst\.|Assistant|Deputy|Joint|Additional|Superintendent)/i,
    /^(OFFICE OF|GST BHAVAN|COMMISSIONERATE|CENTRAL TAX)/i,
    /^(Date|Place|Dated)\s*:/i,
    /^\d{2}[\-\/]\d{2}[\-\/]\d{4}$/,
    /^F\.?\s*No\./i,
  ]
  return patterns.some((p) => p.test(t))
}

function isRomanNumbered(text: string): boolean {
  return /^[IVX]+\.\s+/.test(text.trim())
}

function extractTablesFromMarkdown(markdown: string): {
  cleanedMarkdown: string
  tables: Map<string, { cells: string[]; isHeader: boolean }[]>
} {
  const tables = new Map<string, { cells: string[]; isHeader: boolean }[]>()
  let tableIndex = 0
  const lines = markdown.split("\n")
  const result: string[] = []
  let i = 0

  while (i < lines.length) {
    const line = lines[i].trim()

    if (/^\|.*\|$/.test(line) || /^[^|]*\|[^|]*\|/.test(line)) {
      const tableLines: string[] = []
      while (i < lines.length) {
        const tl = lines[i].trim()
        if (/\|/.test(tl) && tl.length > 2) {
          tableLines.push(tl)
          i++
        } else {
          break
        }
      }

      if (tableLines.length >= 2) {
        const rows: { cells: string[]; isHeader: boolean }[] = []
        let hasSeenSeparator = false

        for (let j = 0; j < tableLines.length; j++) {
          const tl = tableLines[j]
          if (/^\|?\s*[-:]+(\s*\|\s*[-:]+)+\s*\|?$/.test(tl)) {
            hasSeenSeparator = true
            continue
          }
          const cells = tl
            .split("|")
            .map((c) => c.trim())
            .filter((c, idx, arr) => !(idx === 0 && c === "") && !(idx === arr.length - 1 && c === ""))

          if (cells.length > 0) {
            rows.push({ cells, isHeader: !hasSeenSeparator && j === 0 })
          }
        }

        if (rows.length > 0) {
          const placeholder = `__TABLE_PLACEHOLDER_${tableIndex}__`
          tables.set(placeholder, rows)
          result.push(placeholder)
          tableIndex++
        }
      } else {
        tableLines.forEach((tl) => result.push(tl))
      }
    } else {
      result.push(lines[i])
      i++
    }
  }

  return { cleanedMarkdown: result.join("\n"), tables }
}

export interface ParseMarkdownOptions {
  /**
   * Simulator markdown marks headings explicitly with `#`. When true, a bare
   * `N. text` line is always a numbered list item (never a heading, however
   * short), and the only heading source is a `#` line. Default: false (OIO).
   */
  explicitHeadingsOnly?: boolean
}

export function parseMarkdownContent(markdown: string, options: ParseMarkdownOptions = {}): ParsedElement[] {
  const { cleanedMarkdown, tables } = extractTablesFromMarkdown(markdown)
  const elements: ParsedElement[] = []
  const lines = cleanedMarkdown.split("\n")
  let inSignatureBlock = false

  for (let i = 0; i < lines.length; i++) {
    const rawLine = lines[i]
    const trimmed = rawLine.trim()
    if (!trimmed) continue

    if (trimmed.startsWith("__TABLE_PLACEHOLDER_")) {
      const tableData = tables.get(trimmed)
      if (tableData) {
        elements.push({ type: "table", content: "", rows: tableData })
      }
      inSignatureBlock = false
      continue
    }

    const cleaned = lightClean(trimmed)
    if (!cleaned) continue

    const stripped = cleaned.replace(/\*+/g, "").trim()

    const headingMatch = cleaned.match(/^(#{1,6})\s+(.+)$/)
    if (headingMatch) {
      const depth = headingMatch[1].length
      const content = headingMatch[2].replace(/\*+/g, "").trim()
      const majorH = parseMajorHeading(content)
      if (majorH || depth <= 2) {
        elements.push({
          type: "heading",
          content: majorH ? `${majorH.number}. ${majorH.content}` : content,
          level: depth,
        })
      } else {
        elements.push({ type: "subheading", content, level: depth, bold: true })
      }
      inSignatureBlock = false
      continue
    }

    const majorHeading = options.explicitHeadingsOnly ? null : parseMajorHeading(stripped)
    if (majorHeading) {
      elements.push({ type: "heading", content: `${majorHeading.number}. ${majorHeading.content}`, level: 1 })
      inSignatureBlock = false
      continue
    }

    const numbered = parseNumberedParagraph(cleaned)
    if (numbered) {
      elements.push({ type: "numbered", content: numbered.content, number: numbered.number, level: numbered.level })
      inSignatureBlock = false
      continue
    }

    const singleNum = parseSingleNumberParagraph(cleaned, options.explicitHeadingsOnly ? 0 : 80)
    if (singleNum) {
      elements.push({ type: "numbered", content: singleNum.content, number: singleNum.number, level: 0 })
      inSignatureBlock = false
      continue
    }

    if (isRomanNumbered(stripped)) {
      elements.push({ type: "subheading", content: cleaned, bold: true })
      inSignatureBlock = false
      continue
    }

    if (isSignatureBlock(stripped)) {
      inSignatureBlock = true
      elements.push({ type: "signature", content: cleaned })
      continue
    }

    if (inSignatureBlock && cleaned.length < 60) {
      elements.push({ type: "signature", content: cleaned })
      continue
    }

    inSignatureBlock = false

    if (/^[-•]\s+/.test(cleaned)) {
      elements.push({ type: "bullet", content: cleaned.replace(/^[-•]\s+/, "") })
      continue
    }

    const letterItem = cleaned.match(/^\(([a-z]|[ivxlc]+)\)\s+(.+)$/i)
    if (letterItem) {
      elements.push({ type: "bullet", content: letterItem[2], number: letterItem[1] })
      continue
    }

    elements.push({ type: "paragraph", content: cleaned })
  }

  return elements
}

/* ============================================================================
   ELEMENT → DOCX CONVERTER
   ============================================================================ */

export function elementToDocx(element: ParsedElement): (Paragraph | Table)[] {
  switch (element.type) {
    case "heading": {
      return [
        new Paragraph({
          children: createFormattedRuns(element.content, { bold: true, size: SIZES.HEADING, allCaps: true }),
          alignment: AlignmentType.LEFT,
          spacing: { before: SPACING.HEADING_BEFORE, after: SPACING.HEADING_AFTER, line: SPACING.LINE },
        }),
      ]
    }

    case "subheading": {
      return [
        new Paragraph({
          children: createFormattedRuns(element.content, { bold: true, size: SIZES.BODY }),
          alignment: AlignmentType.LEFT,
          spacing: { before: SPACING.PARA_BEFORE + 80, after: SPACING.PARA_AFTER, line: SPACING.LINE },
        }),
      ]
    }

    case "numbered": {
      const level = Math.min(element.level || 0, 6)
      const indentBase = convertInchesToTwip(0.25)
      const indentPerLevel = convertInchesToTwip(0.35)
      const hangingIndent = convertInchesToTwip(0.5)
      const numberText = `${element.number}. `

      return [
        new Paragraph({
          children: [
            createTextRun(numberText, { bold: true, size: SIZES.BODY }),
            ...createFormattedRuns(element.content, { size: SIZES.BODY }),
          ],
          alignment: AlignmentType.JUSTIFIED,
          spacing: {
            before: level === 0 ? SPACING.SECTION_GAP : SPACING.PARA_BEFORE,
            after: SPACING.PARA_AFTER,
            line: SPACING.LINE,
          },
          indent: { left: indentBase + level * indentPerLevel, hanging: hangingIndent },
        }),
      ]
    }

    case "bullet": {
      const label = element.number ? `(${element.number}) ` : "• "
      return [
        new Paragraph({
          children: [
            createTextRun(label, { size: SIZES.BODY }),
            ...createFormattedRuns(element.content, { size: SIZES.BODY }),
          ],
          alignment: AlignmentType.JUSTIFIED,
          spacing: { before: 40, after: 40, line: SPACING.LINE },
          indent: { left: convertInchesToTwip(0.75), hanging: convertInchesToTwip(0.35) },
        }),
      ]
    }

    case "paragraph": {
      return [
        new Paragraph({
          children: createFormattedRuns(element.content, { size: SIZES.BODY }),
          alignment: AlignmentType.JUSTIFIED,
          spacing: { before: SPACING.PARA_BEFORE, after: SPACING.PARA_AFTER, line: SPACING.LINE },
        }),
      ]
    }

    case "signature": {
      const text = element.content
      const isBold =
        /^\(.*\)$/.test(text) ||
        /^\[.*\]$/.test(text) ||
        /^(Commissioner|Asst\.|Assistant|Deputy|Joint|Additional|Superintendent)/i.test(text)
      return [
        new Paragraph({
          children: createFormattedRuns(text, { bold: isBold, size: SIZES.SIGNATURE }),
          alignment: AlignmentType.LEFT,
          spacing: {
            before: text.match(/^\(|^\[/) ? SPACING.SIGNATURE_BEFORE : 20,
            after: 20,
            line: 300,
          },
          indent: { left: convertInchesToTwip(3.5) },
        }),
      ]
    }

    case "table": {
      if (!element.rows || element.rows.length === 0) return []
      const maxCols = Math.max(...element.rows.map((r) => r.cells.length))

      const tableRows = element.rows.map((row) => {
        const cells = [...row.cells]
        while (cells.length < maxCols) cells.push("")

        return new TableRow({
          tableHeader: row.isHeader,
          children: cells.map((cellText) => {
            const isNumeric = /^\s*[\d,]+(\.\d+)?\s*$/.test(cellText) || /Rs\.?\s/i.test(cellText)
            return new TableCell({
              shading: row.isHeader ? { fill: "D9D9D9", type: ShadingType.CLEAR } : undefined,
              verticalAlign: VerticalAlign.CENTER,
              children: [
                new Paragraph({
                  children: createFormattedRuns(cellText.replace(/\*\*/g, ""), {
                    bold: row.isHeader || /\*\*/.test(cellText) || /total/i.test(cellText),
                    size: row.isHeader ? SIZES.TABLE_HEADER : SIZES.TABLE,
                  }),
                  alignment: row.isHeader
                    ? AlignmentType.CENTER
                    : isNumeric
                      ? AlignmentType.RIGHT
                      : AlignmentType.LEFT,
                  spacing: { before: 40, after: 40, line: 260 },
                }),
              ],
              margins: { top: 40, bottom: 40, left: 80, right: 80 },
            })
          }),
        })
      })

      return [
        new Paragraph({ text: "", spacing: { before: 80, after: 0 } }),
        new Table({
          rows: tableRows,
          width: { size: 100, type: WidthType.PERCENTAGE },
          borders: {
            top: BORDERS.TABLE,
            bottom: BORDERS.TABLE,
            left: BORDERS.TABLE,
            right: BORDERS.TABLE,
            insideHorizontal: BORDERS.TABLE,
            insideVertical: BORDERS.TABLE,
          },
        }),
        new Paragraph({ text: "", spacing: { before: 0, after: 40 } }),
      ]
    }

    case "spacer": {
      return [new Paragraph({ text: "", spacing: { before: 120, after: 0 } })]
    }

    default:
      return []
  }
}

/** Paragraph/run styles shared by every OIO-styled export. */
export function oioDocumentStyles(): DocumentOptions["styles"] {
  return {
    default: {
      document: {
        run: { font: FONT, size: SIZES.BODY },
        paragraph: {
          spacing: { line: SPACING.LINE, before: SPACING.PARA_BEFORE, after: SPACING.PARA_AFTER },
        },
      },
    },
    paragraphStyles: [
      {
        id: "Normal",
        name: "Normal",
        run: { font: FONT, size: SIZES.BODY },
        paragraph: {
          spacing: { line: SPACING.LINE, before: SPACING.PARA_BEFORE, after: SPACING.PARA_AFTER },
          alignment: AlignmentType.JUSTIFIED,
        },
      },
      {
        id: "Heading1",
        name: "Heading 1",
        basedOn: "Normal",
        run: { font: FONT, size: SIZES.HEADING, bold: true, allCaps: true },
        paragraph: {
          spacing: { before: SPACING.HEADING_BEFORE, after: SPACING.HEADING_AFTER, line: SPACING.LINE },
          alignment: AlignmentType.LEFT,
        },
      },
      {
        id: "Heading2",
        name: "Heading 2",
        basedOn: "Normal",
        run: { font: FONT, size: SIZES.BODY, bold: true },
        paragraph: {
          spacing: { before: SPACING.PARA_BEFORE + 60, after: SPACING.PARA_AFTER, line: SPACING.LINE },
          alignment: AlignmentType.LEFT,
        },
      },
    ],
  }
}

/** Page geometry shared by every OIO-styled export. */
export const OIO_PAGE = {
  margin: {
    top: convertInchesToTwip(1),
    right: convertInchesToTwip(1),
    bottom: convertInchesToTwip(1),
    left: convertInchesToTwip(1.25),
  },
}

/** Legal (1., 1.1., ...) numbering config shared by every OIO-styled export. */
export function oioLegalNumbering(): DocumentOptions["numbering"] {
  return {
    config: [
      {
        reference: "legal-numbering",
        levels: Array.from({ length: 7 }).map((_, i) => ({
          level: i,
          format: LevelFormat.DECIMAL,
          text:
            i === 0
              ? "%1."
              : Array.from({ length: i + 1 }, (_, j) => `%${j + 1}`).join(".") + ".",
          alignment: AlignmentType.LEFT,
          style: {
            paragraph: {
              indent: {
                left: convertInchesToTwip(0.5 + i * 0.25),
                hanging: convertInchesToTwip(0.25),
              },
            },
          },
        })),
      },
    ],
  }
}

/* ============================================================================
   MAIN DOCUMENT CREATOR
   ============================================================================ */

export async function createGSTOIODocument(markdown: string): Promise<Blob> {
  const elements = parseMarkdownContent(markdown)
  const children = elements.flatMap(elementToDocx)

  const doc = new Document({
    styles: oioDocumentStyles(),
    numbering: oioLegalNumbering(),
    sections: [
      {
        properties: {
          page: OIO_PAGE,
        },
        headers: {
          default: new Header({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: "ORDER-IN-ORIGINAL",
                    font: FONT,
                    size: SIZES.TITLE,
                    bold: true,
                    allCaps: true,
                  }),
                ],
                alignment: AlignmentType.CENTER,
                spacing: { after: 200 },
                border: {
                  bottom: { color: "000000", space: 1, style: BorderStyle.SINGLE, size: 8 },
                },
              }),
            ],
          }),
        },
        footers: {
          default: new Footer({
            children: [
              new Paragraph({
                children: [
                  new TextRun({ text: "Page ", font: FONT, size: SIZES.FOOTER }),
                  new TextRun({ children: [PageNumber.CURRENT], font: FONT, size: SIZES.FOOTER }),
                  new TextRun({ text: " of ", font: FONT, size: SIZES.FOOTER }),
                  new TextRun({ children: [PageNumber.TOTAL_PAGES], font: FONT, size: SIZES.FOOTER }),
                ],
                alignment: AlignmentType.CENTER,
                border: {
                  top: { color: "CCCCCC", space: 1, style: BorderStyle.SINGLE, size: 4 },
                },
                spacing: { before: 100 },
              }),
            ],
          }),
        },
        children,
      },
    ],
  })

  return Packer.toBlob(doc)
}

/* ============================================================================
   METADATA EXTRACTION
   ============================================================================ */

export function extractGSTOIOMetadata(md: string): {
  orderNumber?: string
  assesseeName?: string
  date?: string
} {
  const orderMatch = md.match(/SCN\s*(?:No\.?|Number)?\s*:?\s*([A-Z0-9\-\/]+)/i)
  const assesseeMatch = md.match(/(?:Noticee|Assessee)[,:]?\s*([^,\n]+)/i)
  const dateMatch = md.match(/Date:\s*(\d{2}[\-\/]\d{2}[\-\/]\d{4})/i)

  return {
    orderNumber: orderMatch?.[1],
    assesseeName: assesseeMatch?.[1]?.trim(),
    date: dateMatch?.[1],
  }
}
